import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
    getInvoiceRequestCustomerAttConf,
    getInvoiceRequestDetails,
    getInvoiceRequestInvChangeTypeList,
    validateBlNumber,
} from './InvoiceRequestActions';

interface IInvoiceRequestDocAttachmentList {
    list: any[];
    next: string | null;
    previous: string | null;
}

export interface IInvoiceRequestState {
    dataLoading: boolean;
    validateBlLoading: boolean;
    validatedBlDetails: any;
    invoiceRequestFormView: {
        invoiceRequestId: any;
        blId: any;
        requestedPartnerId: any;
        status: any;
        attList: any[];
        invoiceTypeAccessDtlsList: any[];
    };
    customerAttData: IInvoiceRequestDocAttachmentList;
    customerDocList: any[] | null;
    customerDropdownDocList: any[] | null;
    selectedCustomerAttachmentName: any;
    selectedCustomerAttachmentFilePath: any;
    selectedCustomerFileServerPath: any;
    invoiceTypeList: Array<any>;
}

const initialState: IInvoiceRequestState = {
    dataLoading: false,
    validateBlLoading: false,
    validatedBlDetails: {},
    invoiceRequestFormView: {
        invoiceRequestId: null,
        blId: null,
        requestedPartnerId: null,
        status: null,
        attList: [],
        invoiceTypeAccessDtlsList: [],
    },
    customerAttData: { list: [], next: null, previous: null },
    customerDocList: null,
    customerDropdownDocList: null,
    selectedCustomerAttachmentName: {},
    selectedCustomerAttachmentFilePath: {},
    selectedCustomerFileServerPath: {},
    invoiceTypeList: [],
};

function mapAttConfPayload(payload: any[]) {
    const updatedDocList =
        (payload
            ?.filter((i: any) => i?.isMandatory != 'H' && i?.isMandatory != 'E')
            ?.map((i: any) => ({
                rowNumber: crypto.randomUUID(),
                id: i?.docId,
                name: i?.docName,
                m: i?.isMandatory == 'Y',
            })) as any[]) ?? [];

    const dropdownOptions =
        (payload
            ?.filter((i: any) => i?.isMandatory == 'H')
            ?.map((i: any) => ({
                id: i?.docId,
                name: i?.docName,
                m: true,
                isDropDownDoc: true,
            })) as any[]) ?? [];

    const attList = (updatedDocList ?? [])?.map((x: any) => ({
        rowNumber: x.rowNumber,
        attachmentId: null,
        docId: x.id,
        fileName: '',
        filePath: null,
        m: x.m,
        ...(x.hasOwnProperty('isDropDownDoc') ? { isDropDownDoc: x.isDropDownDoc } : {}),
    }));

    return { updatedDocList, dropdownOptions, attList };
}

export const InvoiceRequestSlice = createSlice({
    name: 'invoiceRequest',
    initialState,
    reducers: {
        updateSelectedCustomerAttachmentName: (state, action: PayloadAction<any>) => {
            state.selectedCustomerAttachmentName = action.payload;
        },
        updateCustomerAttData: (state, action: PayloadAction<any>) => {
            state.customerAttData.list = action.payload;
        },
        updateSelectedCustomerAttachmentFilePath: (state, action: PayloadAction<any>) => {
            state.selectedCustomerAttachmentFilePath = action.payload;
        },
        updateSelectedCustomerFileServerPath: (state, action: PayloadAction<any>) => {
            state.selectedCustomerFileServerPath = action.payload;
        },
        clearCustomerFields: (state) => {
            state.customerDropdownDocList = null;
            state.selectedCustomerAttachmentName = {};
            state.selectedCustomerAttachmentFilePath = {};
            state.selectedCustomerFileServerPath = {};
        },
        clearInvoiceRequestViewAndCustomerState: (state) => {
            state.invoiceRequestFormView = {
                invoiceRequestId: null,
                blId: null,
                requestedPartnerId: null,
                status: null,
                attList: [],
                invoiceTypeAccessDtlsList: [],
            };
            // state.customerAttData = { list: [], next: null, previous: null };
            // state.customerDocList = null;
            // state.customerDropdownDocList = null;
            // state.selectedCustomerAttachmentName = {};
            // state.selectedCustomerAttachmentFilePath = {};
            // state.selectedCustomerFileServerPath = {};
        },
        updateInvoiceType: (state, action: PayloadAction<any>) => {
            const row = state.invoiceTypeList.find((i: any) => i.value == action.payload.code);
            if (row) row[action.payload.key] = action.payload.value;
        },
        resetInvoiceTypeList: (state) => {
            state.invoiceTypeList =
                state.invoiceTypeList?.map((i: any) => ({
                    ...i,
                    allowAccess: false,
                })) ?? [];
        },
        removeCustomerDocument: (state, action: PayloadAction<{ rowNumber: string }>) => {
            const { rowNumber: rowNumberToRemove } = action.payload;
            state.customerDocList = (state.customerDocList ?? []).filter(
                (doc) => doc.rowNumber !== rowNumberToRemove
            );
            state.customerAttData.list = (state.customerAttData.list ?? []).filter(
                (att) => att.rowNumber !== rowNumberToRemove
            );
            const { [rowNumberToRemove]: _rn, ...restName } = state.selectedCustomerAttachmentName ?? {};
            state.selectedCustomerAttachmentName = Object.keys(restName).length > 0 ? restName : {};
            const { [rowNumberToRemove]: _rf, ...restFilePath } =
                state.selectedCustomerAttachmentFilePath ?? {};
            state.selectedCustomerAttachmentFilePath =
                Object.keys(restFilePath).length > 0 ? restFilePath : {};
            const { [rowNumberToRemove]: _rs, ...restServerPath } =
                state.selectedCustomerFileServerPath ?? {};
            state.selectedCustomerFileServerPath =
                Object.keys(restServerPath).length > 0 ? restServerPath : {};
        },
        clearValidatedBlDetails: (state) => {
            state.validatedBlDetails = {};
            state.validateBlLoading = false;
        },
        updateCustomerListByRowNumber: (state, action: PayloadAction<{ rowNumber: string; updatedFields: { id: number; name: string } }>) => {
            const { rowNumber, updatedFields } = action.payload;
            const dList = state.customerDocList;
            if (!dList) return;
            const docListIndex = dList.findIndex((doc) => doc.rowNumber === rowNumber);
            if (docListIndex !== -1) {
                dList[docListIndex] = { ...dList[docListIndex], ...updatedFields };
            }
            if (state.customerAttData?.list) {
                const attIndex = state.customerAttData.list.findIndex(
                    (att) => att.rowNumber === rowNumber
                );
                if (attIndex !== -1) {
                    state.customerAttData.list[attIndex] = {
                        ...state.customerAttData.list[attIndex],
                        docId: updatedFields.id,
                    };
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getInvoiceRequestDetails.fulfilled, (state, action) => {
            const payload: any = action.payload?.data ?? {};

            state.dataLoading = false;
            state.customerDropdownDocList = payload?.dropdownDocList ?? [];
            state.customerDocList = payload?.updatedDocListData ?? [];
            state.customerAttData.list = payload?.attList ?? [];
            state.invoiceRequestFormView = {
                invoiceRequestId: payload?.invoiceRequestId ?? null,
                blId: payload?.blId ?? null,
                requestedPartnerId: payload?.requestedPartnerId ?? null,
                status: payload?.status ?? null,
                attList: payload?.attList ?? [],
                invoiceTypeAccessDtlsList: payload?.invoiceTypeAccessDtlsList ?? [],
            };

            state.invoiceTypeList.forEach((row: any) => {
                const data = payload?.invoiceTypeAccessDtlsList?.find(
                    (item: any) => item.type == row.value
                );
                row.allowAccess = data?.allowAccess == 'Y';
                row.invoiceTypeAccessId = data?.invoiceTypeAccessId ?? null;
                row.invoiceRequestInstructionId = data?.invoiceRequestInstructionId ?? null;
            });
        });
        builder.addCase(getInvoiceRequestInvChangeTypeList.pending, (state) => {
            state.invoiceTypeList = [];
        });
        builder.addCase(getInvoiceRequestInvChangeTypeList.fulfilled, (state, action) => {
            state.invoiceTypeList =
                action.payload?.map((i: any) => ({
                    value: i.value,
                    label: i.label,
                    text: i.label,
                    allowAccess: false,
                })) ?? [];

            const saved = state.invoiceRequestFormView?.invoiceTypeAccessDtlsList ?? [];
            state.invoiceTypeList.forEach((row: any) => {
                const data = saved.find((item: any) => item.type == row.value);
                row.allowAccess = data?.allowAccess == 'Y';
                row.invoiceTypeAccessId = data?.invoiceTypeAccessId ?? null;
                row.invoiceRequestInstructionId = data?.invoiceRequestInstructionId ?? null;
            });
        });
        builder.addCase(getInvoiceRequestCustomerAttConf.fulfilled, (state, action) => {
            const { updatedDocList, dropdownOptions, attList } = mapAttConfPayload(
                action.payload ?? []
            );
            state.customerDropdownDocList = dropdownOptions;
            state.customerDocList = updatedDocList;
            state.customerAttData.list = attList;
        });
        builder.addCase(validateBlNumber.pending, (state) => {
            state.validateBlLoading = true;
        });
        builder.addCase(validateBlNumber.fulfilled, (state, action) => {
            state.validateBlLoading = false;
            state.validatedBlDetails = action.payload ?? {};
        });
        builder.addCase(validateBlNumber.rejected, (state) => {
            state.validateBlLoading = false;
            state.validatedBlDetails = {};
        });
    },
});

export const {
    updateSelectedCustomerAttachmentName,
    updateCustomerAttData,
    updateSelectedCustomerAttachmentFilePath,
    updateSelectedCustomerFileServerPath,
    clearCustomerFields,
    clearInvoiceRequestViewAndCustomerState,
    removeCustomerDocument,
    updateCustomerListByRowNumber,
    updateInvoiceType,
    resetInvoiceTypeList,
    clearValidatedBlDetails,
} = InvoiceRequestSlice.actions;

export default InvoiceRequestSlice.reducer;
